import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, X, Image as ImageIcon, Upload } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Resolve relative paths (e.g. /api/uploads/foo.jpg) to absolute URLs for previews.
const absolutize = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (/^https?:\/\//i.test(url) || url.startsWith('blob:') || url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return url;
};

const isVideoUrl = (url) => /\.(mp4|mov|webm|m4v)(\?.*)?$/i.test(url || '');

/**
 * Reusable media URL list editor.
 * Props:
 *   urls: string[]            — current list of URLs/paths
 *   onChange: (urls) => void  — callback with the new list
 *   helperText: string?       — override the default helper text
 *   testIdPrefix: string?     — prefix for data-testids (default 'media')
 *   accept: string?           — file picker accept attr (default 'image/*,video/*')
 *   multiple: boolean?        — allow multi-file pick (default true)
 */
const MediaUrlsInput = ({
  urls = [],
  onChange,
  helperText = 'Upload files directly, or paste image/video URLs or YouTube/Vimeo links. The first item is the cover.',
  testIdPrefix = 'media',
  accept = 'image/*,video/*',
  multiple = true,
}) => {
  const [newUrl, setNewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const addUrl = () => {
    const trimmed = newUrl.trim();
    if (!trimmed) return;
    onChange([...urls, trimmed]);
    setNewUrl('');
  };

  const removeUrl = (index) => onChange(urls.filter((_, i) => i !== index));

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const token = localStorage.getItem('token');
    setUploading(true);
    const newPaths = [];

    try {
      for (const file of Array.from(fileList)) {
        if (file.size > 200 * 1024 * 1024) {
          toast.error(`${file.name} is larger than 200 MB — please host externally.`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/upload`, fd, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        });
        if (res.data?.url) newPaths.push(res.data.url);
      }

      if (newPaths.length > 0) {
        onChange([...urls, ...newPaths]);
        toast.success(`Uploaded ${newPaths.length} file${newPaths.length > 1 ? 's' : ''}`);
      }
    } catch (err) {
      console.error(err);
      toast.error('Upload failed. Check the file size and try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3">
      <Label>Images / Videos</Label>
      <p className="text-xs text-maroon/50">{helperText}</p>

      {urls.length > 0 && (
        <div className="space-y-2">
          {urls.map((url, index) => (
            <div
              key={index}
              className="flex items-center gap-2 bg-sand/30 border border-maroon/10 rounded-sm p-2"
              data-testid={`${testIdPrefix}-row-${index}`}
            >
              <span className="text-xs text-maroon/40 font-mono w-5 text-center flex-shrink-0">{index + 1}</span>
              {isVideoUrl(url) ? (
                <div className="w-12 h-12 flex items-center justify-center rounded-sm border border-maroon/10 bg-maroon/10 text-maroon text-[10px] font-bold flex-shrink-0">
                  VIDEO
                </div>
              ) : (
                <img
                  src={absolutize(url)}
                  alt={`Media ${index + 1}`}
                  className="w-12 h-12 object-cover rounded-sm border border-maroon/10 flex-shrink-0 bg-white"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <span className="text-sm text-maroon/70 truncate flex-1">{url}</span>
              <button
                type="button"
                onClick={() => removeUrl(index)}
                className="p-1 text-coral hover:bg-coral/10 rounded-sm flex-shrink-0"
                data-testid={`remove-${testIdPrefix}-${index}`}
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          value={newUrl}
          onChange={(e) => setNewUrl(e.target.value)}
          placeholder="Paste image, video, YouTube or Vimeo URL..."
          className="input-field flex-1"
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addUrl(); } }}
          data-testid={`${testIdPrefix}-url-input`}
        />
        <Button
          type="button"
          onClick={addUrl}
          className="bg-terracotta text-white hover:bg-terracotta/90 px-4"
          data-testid={`add-${testIdPrefix}-url-btn`}
        >
          <Plus size={16} className="mr-1" /> Add
        </Button>
        <Button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          variant="outline"
          className="border-maroon/30 text-maroon hover:bg-maroon/5 px-4"
          data-testid={`upload-${testIdPrefix}-btn`}
        >
          <Upload size={16} className="mr-1" />
          {uploading ? 'Uploading…' : 'Upload File'}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid={`${testIdPrefix}-file-input`}
        />
      </div>

      {urls.length === 0 && (
        <div className="border-2 border-dashed border-maroon/15 rounded-sm p-6 text-center">
          <ImageIcon size={32} className="mx-auto text-maroon/20 mb-2" />
          <p className="text-sm text-maroon/40">No media added yet. Upload a file or paste a URL above.</p>
        </div>
      )}
    </div>
  );
};

export { MediaUrlsInput, absolutize, isVideoUrl };
export default MediaUrlsInput;
